import { LitElement, html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

/**
 * Tag names whose direct-child instances bleed flush to the card's inner border edges.
 * Stored as a module-level Set so it is allocated once, not per-instance.
 */
const FLUSH_TAGS = new Set([
  'asc-divider',
  'img',
  'picture',
  'figure',
  'asc-accordion-panel',
  'asc-accordion-group',
]);

/**
 * The CSS value used for both the card's inner padding and the inverse offset applied
 * to flush children. Expressed as a CSS custom-property reference with a 24px fallback
 * so it resolves correctly in inline style values, which are also subject to variable
 * resolution.
 */
const FLUSH_PADDING = 'var(--asc-spacing-3xl, 24px)';

/**
 * Inline style properties that the card overrides on flush children.
 * Stored once at module level so the array is shared across all instances.
 */
const FLUSH_PROPS = [
  'display',
  'margin-inline-start',
  'margin-inline-end',
  'width',
  'max-width',
  'margin-top',
  'margin-bottom',
] as const;

/**
 * A locked-style surface container for any consumer content.
 *
 * Enforces the standard Ascensus card visual treatment — white background,
 * 1px border, 16px border-radius, and a multi-layer skeuomorphic drop shadow.
 *
 * Supports two layout modes via the `layout` property:
 * - **`vertical`** (default) — inner padding is uniform on all sides; body content
 *   fills the card.
 * - **`horizontal`** — a fixed-width image panel sits on the leading side; the
 *   body content fills the remaining space. Use the `image` named slot for the
 *   thumbnail.
 *
 * Consumers own all interior typography and spacing via slotted content.
 *
 * ### Flush children
 *
 * When a **direct child** of the default slot is one of the following element types —
 * `asc-divider`, `img`, `picture`, `figure`, `asc-accordion-panel`,
 * `asc-accordion-group` — the card applies inline styles via a `slotchange` handler
 * so the element's edges bleed flush to the card's inner border:
 *
 * - **Horizontal bleed** — always applied to flush-eligible direct children.
 * - **Top bleed** — applied when the flush-eligible element is the first assigned child.
 * - **Bottom bleed** — applied when the flush-eligible element is the last assigned child.
 *
 * Inline styles are used (rather than `::slotted()` CSS) because `base.css` sets
 * `img, svg { max-width: 100% }` as a light DOM author rule, which outranks shadow-DOM
 * `::slotted()` styles in the cascade. Inline styles are always highest priority and
 * cleanly override this constraint. Styles are removed when the element leaves the slot.
 *
 * `overflow: hidden` on `[part=base]` clips flush children cleanly at the rounded corners.
 *
 * ### Horizontal layout
 *
 * Set `layout="horizontal"` to place a fixed-width image panel on the leading side
 * and the body content in the trailing panel. Use the `image` slot for the thumbnail
 * and the default slot for headings, descriptions, and any other body content.
 * The image panel is hidden automatically when the `image` slot is empty.
 * On viewports ≤ 767 px the card collapses back to vertical automatically.
 *
 * ```html
 * <!-- Basic card (vertical, default) -->
 * <asc-card>
 *   <h2>Plan summary</h2>
 *   <p>Your current balance is $42,350.00.</p>
 * </asc-card>
 *
 * <!-- Card with flush top image -->
 * <asc-card>
 *   <img src="/hero.jpg" alt="Fund overview" width="600" height="300">
 *   <h2>Retirement overview</h2>
 *   <p>Review your fund performance for the quarter.</p>
 * </asc-card>
 *
 * <!-- Horizontal card -->
 * <asc-card layout="horizontal">
 *   <img slot="image" src="/thumbnail.jpg" alt="" />
 *   <h3>Year-end tax deadlines every IRA holder should know</h3>
 *   <p>With the April filing deadline approaching…</p>
 * </asc-card>
 * ```
 *
 * @element asc-card
 *
 * @slot - The primary content area. Accepts any elements. Consumers own all interior layout.
 * @slot image - Thumbnail image for `layout="horizontal"`. Accepts `<img>` or `<picture>`.
 *   Hidden automatically when empty. Has no effect in `layout="vertical"`.
 *
 * @csspart base  - The root Shadow DOM wrapper. Carries all visual treatment (border,
 *                 radius, shadow, background). In `vertical` mode also carries padding.
 *                 Target via `::part(base)` for dimension overrides (e.g., `max-width`).
 * @csspart image - The image panel in `layout="horizontal"`. Controls the panel width.
 *                 Override `--asc-card-image-width` to resize.
 * @csspart body  - The body panel in `layout="horizontal"`. Carries the content padding.
 *
 * @cssprop --asc-card-image-width - Width of the leading image panel in `horizontal` layout.
 *   Default: `320px`
 * @cssprop --asc-card-min-height  - Minimum height of the card in `horizontal` layout.
 *   Default: `220px`
 */
@customElement('asc-card')
export class AscCard extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    [part='base'] {
      background: var(--asc-bg-primary);
      border: var(--asc-border-width) solid var(--asc-border-secondary);
      border-radius: var(--asc-radius-lg);
      box-shadow:
        0 0 1px 1px var(--asc-shadow-skeumorphic-inner) inset,
        0 -1px 1px 1px var(--asc-shadow-skeumorphic-inner) inset,
        0 1px 2px 0 var(--asc-shadow-xs);
      overflow: hidden;
      height: 100%;
    }

    /* ── Base ─────────────────────────────────────────────────────────── */
    /* Always flex so image/body panels work in both modes. */
    [part='base'] {
      display: flex;
    }

    /* ── Vertical layout (default) ───────────────────────────────────── */
    :host(:not([layout='horizontal'])) [part='base'] {
      flex-direction: column;
      padding: var(--asc-spacing-3xl, 24px);
    }

    /* image slot hidden in vertical — body fills the whole card */
    :host(:not([layout='horizontal'])) [part='image'] {
      display: none;
    }

    :host(:not([layout='horizontal'])) [part='body'] {
      flex: 1;
    }

    /* ── Horizontal layout ───────────────────────────────────────────── */
    :host([layout='horizontal']) [part='base'] {
      flex-direction: row;
      min-height: var(--asc-card-min-height, 220px);
    }

    :host([layout='horizontal']) [part='image'] {
      display: flex;
      width: var(--asc-card-image-width, 320px);
      flex-shrink: 0;
      align-self: stretch;
      overflow: hidden;
    }

    :host([layout='horizontal']) [part='image'][hidden] {
      display: none;
    }

    :host([layout='horizontal']) [part='image'] ::slotted(img),
    :host([layout='horizontal']) [part='image'] ::slotted(picture) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      flex: 1;
    }

    :host([layout='horizontal']) [part='body'] {
      display: flex;
      flex-direction: column;
      padding: var(--asc-spacing-3xl, 24px);
      flex: 1;
      min-width: 0;
    }

    /* ── Mobile: collapse horizontal to vertical at ≤ 767px ─────────── */
    @media (max-width: 767px) {
      :host([layout='horizontal']) [part='base'] {
        flex-direction: column;
        min-height: unset;
      }

      :host([layout='horizontal']) [part='image'] {
        width: 100%;
        aspect-ratio: 16 / 9;
        height: auto;
      }
    }
  `;

  /**
   * Controls the arrangement of the image and body panels.
   * - `vertical` — body content fills the entire card (default, existing behaviour)
   * - `horizontal` — image occupies a fixed-width leading panel; body fills the rest
   */
  @property({ type: String, reflect: true }) layout: 'vertical' | 'horizontal' = 'vertical';

  /** Tracks whether the `image` slot has any assigned nodes (horizontal mode only). */
  @state() private _hasImage = false;

  /** Tracks elements currently holding flush inline styles so they can be cleaned up. */
  private _flushedElements = new Set<HTMLElement>();

  /** Snapshots of consumer-provided inline style values, keyed by element, so they can be restored on cleanup. */
  private _savedStyles = new WeakMap<HTMLElement, Map<string, string>>();

  @query('slot:not([name])') private _slot!: HTMLSlotElement;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._clearAllFlushStyles();
  }

  // Arrow function so `this` is always bound when passed as an event listener.
  private _handleSlotChange = (): void => {
    this._applyFlushStyles();
  };

  private _handleImageSlotChange = (e: Event): void => {
    const slot = e.target as HTMLSlotElement;
    this._hasImage = slot.assignedNodes({ flatten: true }).length > 0;
  };

  private _applyFlushStyles(): void {
    if (!this._slot) return;

    const elements = this._slot.assignedElements();
    const nextFlushed = new Set<HTMLElement>();

    elements.forEach((el, index) => {
      if (!FLUSH_TAGS.has(el.tagName.toLowerCase())) return;

      const htmlEl = el as HTMLElement;
      const isFirst = index === 0;
      const isLast = index === elements.length - 1;

      // Snapshot pre-existing inline values on first application so they can be
      // restored exactly when the element leaves the slot or flush no longer applies.
      if (!this._savedStyles.has(htmlEl)) {
        const snapshot = new Map<string, string>();
        for (const prop of FLUSH_PROPS) {
          snapshot.set(prop, htmlEl.style.getPropertyValue(prop));
        }
        this._savedStyles.set(htmlEl, snapshot);
      }

      const saved = this._savedStyles.get(htmlEl)!;

      // Inline styles override all stylesheet rules (including the global
      // `img, svg { max-width: 100% }` reset in base.css).
      htmlEl.style.setProperty('display', 'block');
      htmlEl.style.setProperty('margin-inline-start', `calc(-1 * ${FLUSH_PADDING})`);
      htmlEl.style.setProperty('margin-inline-end', `calc(-1 * ${FLUSH_PADDING})`);
      htmlEl.style.setProperty('width', `calc(100% + 2 * ${FLUSH_PADDING})`);
      htmlEl.style.setProperty('max-width', 'none');

      if (isFirst) {
        htmlEl.style.setProperty('margin-top', `calc(-1 * ${FLUSH_PADDING})`);
      } else {
        // Restore the consumer's original margin-top (if any) when the element
        // is no longer the first child, instead of blindly removing the property.
        const orig = saved.get('margin-top') ?? '';
        if (orig) {
          htmlEl.style.setProperty('margin-top', orig);
        } else {
          htmlEl.style.removeProperty('margin-top');
        }
      }

      if (isLast) {
        htmlEl.style.setProperty('margin-bottom', `calc(-1 * ${FLUSH_PADDING})`);
      } else {
        // Same restore logic for margin-bottom.
        const orig = saved.get('margin-bottom') ?? '';
        if (orig) {
          htmlEl.style.setProperty('margin-bottom', orig);
        } else {
          htmlEl.style.removeProperty('margin-bottom');
        }
      }

      nextFlushed.add(htmlEl);
    });

    // Remove flush styles from any element that left the slot since the last update.
    for (const el of this._flushedElements) {
      if (!nextFlushed.has(el)) {
        this._clearElementFlushStyles(el);
      }
    }

    this._flushedElements = nextFlushed;
  }

  private _clearAllFlushStyles(): void {
    for (const el of this._flushedElements) {
      this._clearElementFlushStyles(el);
    }
    this._flushedElements.clear();
  }

  private _clearElementFlushStyles(el: HTMLElement): void {
    const saved = this._savedStyles.get(el);
    for (const prop of FLUSH_PROPS) {
      const original = saved?.get(prop) ?? '';
      if (original) {
        el.style.setProperty(prop, original);
      } else {
        el.style.removeProperty(prop);
      }
    }
    this._savedStyles.delete(el);
  }

  override render() {
    return html`
      <div part="base">
        <div part="image" ?hidden=${!this._hasImage}>
          <slot name="image" @slotchange=${this._handleImageSlotChange}></slot>
        </div>
        <div part="body">
          <slot @slotchange=${this._handleSlotChange}></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-card': AscCard;
  }
}
